import React from "react";
import styled from "styled-components";
import { C, F, CARD_RING, EASE, ring, pulsing, Button } from "../styles";
import { IconBroadcast, IconCalendar, IconCheck, IconLock } from "./Icons";
import { AREAS, BLOCKS, TOTAL_QUESTIONS } from "../data/calendar";

const Blocks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Block = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BlockHead = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const BlockNum = styled.span`
  font: 400 15px/1 ${F.num};
  letter-spacing: 0.06em;
  color: ${C.blue};
  background: ${C.yellow};
  border-radius: 6px;
  padding: 6px 9px 5px;
`;

const BlockTitle = styled.span`
  font: 700 18px/1.2 ${F.title};
  letter-spacing: -0.02em;
`;

const Rule = styled.span`
  flex: 1 1 40px;
  height: 1px;
  background: rgba(255, 255, 255, 0.14);
`;

const Dates = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 10px;
`;

const Stack = styled.span`
  display: flex;
  flex-direction: column;
  gap: ${(p) => p.gap || 3}px;
  align-items: ${(p) => p.align || "flex-start"};
`;

const Overline = styled.span`
  font: 600 10px/1 ${F.title};
  letter-spacing: 0.12em;
  color: rgba(255, 255, 255, 0.55);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(min(100%, 260px), 1fr));
  gap: 12px;
`;

const Card = styled.div`
  border-radius: 16px;
  padding: 18px 20px;
  background: ${C.navy};
  box-shadow: ${(p) => (p.active ? ring(1.5, C.yellow) : CARD_RING)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
  transition: transform 240ms ${EASE}, box-shadow 240ms ${EASE};
  &:hover {
    transform: translateY(-4px);
    box-shadow: ${ring(1.5, C.yellow)}, 0 0 0 4px rgba(255, 255, 0, 0.12);
  }
  @media (prefers-reduced-motion: reduce) {
    &:hover {
      transform: none;
    }
  }
`;

const TAGS = {
  live: { bg: "rgba(255,82,39,0.14)", fg: C.white, ring: ring(1, C.orange) },
  done: { bg: "rgba(52,199,89,0.16)", fg: "#7EE2A0", ring: ring(1, "#34C759") },
  locked: { bg: "transparent", fg: "rgba(255,255,255,0.6)", ring: ring(1, "rgba(255,255,255,0.2)") },
  expired: { bg: "transparent", fg: "rgba(255,255,255,0.6)", ring: ring(1, "rgba(255,255,255,0.2)") },
};

const Tag = styled.span`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px;
  border-radius: 20px;
  font: 600 11px/1 ${F.title};
  background: ${(p) => TAGS[p.status].bg};
  color: ${(p) => TAGS[p.status].fg};
  box-shadow: ${(p) => TAGS[p.status].ring};
`;

const Pulse = styled.span`
  display: inline-flex;
  ${pulsing}
`;

const AreaName = styled.span`
  font: 700 19px/1.2 ${F.title};
`;

const Progress = styled.span`
  font: 600 12px/1 ${F.title};
  color: ${C.pale};
`;

const OpensAt = styled.span`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  padding: 8px 12px;
  border-radius: 10px;
  background: ${C.ink};
  box-shadow: ${CARD_RING};
`;

const BigDate = styled.span`
  font: 400 ${(p) => p.size || 20}px/1 ${F.num};
  letter-spacing: 0.03em;
  color: ${C.white};
`;

function SimCard({ area, item, onStart, onSeeGabarito }) {
  const { status, answered, score } = item;
  const started = status === "live" && answered > 0;
  return (
    <Card active={status === "live"}>
      <Stack gap={6}>
        <Tag status={status}>
          {status === "live" && (
            <>
              <Pulse>
                <IconBroadcast />
              </Pulse>
              NO AR
            </>
          )}
          {status === "done" && (
            <>
              <IconCheck size={12} width={3} />
              FEITO · {score}/{TOTAL_QUESTIONS}
            </>
          )}
          {status === "locked" && (
            <>
              <IconLock />
              EM BREVE
            </>
          )}
          {status === "expired" && "ENCERRADO"}
        </Tag>
        <AreaName>{area.name}</AreaName>
        {started && (
          <Progress>
            {answered} de {TOTAL_QUESTIONS} respondidas · salvo
          </Progress>
        )}
      </Stack>
      {status === "live" && (
        <Button variant="yellow" onClick={() => onStart(area)}>
          {started ? "Continuar simulado →" : "Começar simulado →"}
        </Button>
      )}
      {(status === "done" || status === "expired") && (
        <Button variant="outline" pad="12px 16px" onClick={() => onSeeGabarito(area)}>
          Ver gabarito
        </Button>
      )}
      {status === "locked" && (
        <OpensAt>
          <Overline>ENTRA NO AR</Overline>
          <BigDate>{area.openLabel} · 00H</BigDate>
        </OpensAt>
      )}
    </Card>
  );
}

// items: { [slug]: { status, answered, score } }
export default function Schedule({ items, onStart, onSeeGabarito }) {
  return (
    <Blocks>
      {BLOCKS.map((b) => (
        <Block key={b.id}>
          <BlockHead>
            <BlockNum>{b.num}</BlockNum>
            <BlockTitle>{b.title}</BlockTitle>
            <Rule />
            <Dates>
              <IconCalendar color={C.light} size={16} width={2.2} />
              <Stack>
                <Overline>DISPONÍVEL</Overline>
                <BigDate size={18}>{b.dates}</BigDate>
              </Stack>
            </Dates>
          </BlockHead>
          <Grid>
            {AREAS.filter((a) => a.block === b.id).map((a) => (
              <SimCard key={a.slug} area={a} item={items[a.slug]} onStart={onStart} onSeeGabarito={onSeeGabarito} />
            ))}
          </Grid>
        </Block>
      ))}
    </Blocks>
  );
}
